import { useState } from 'react';
import { Trash, ArrowSquareOut } from '@phosphor-icons/react';
import { useRatingsHistory, useDeleteFideRating, type FideCadence } from '@/api/ratings';

/**
 * FideHistoryTable — historique paginé des records FIDE saisis manuellement.
 *
 * 3 sections (Standard / Rapide / Blitz). Bouton "Supprimer" sur chaque ligne
 * pour corriger une saisie erronée. Pas de pagination réelle pour l'instant
 * (volume Léon = ~10 saisies/an max, scroll suffit jusqu'à MVP5).
 *
 * Hypothèse : l'endpoint `/users/me/ratings/history?platform=fide&cadence=X`
 * retourne `points: [{rated_at, value}]` — pour avoir source_label/note il
 * faudrait un endpoint étendu (T6 backend follow-up éventuel). Ici on
 * affiche juste date + valeur + bouton supprimer.
 */

const CADENCES: FideCadence[] = ['standard', 'rapide', 'blitz'];
const CADENCE_LABEL: Record<FideCadence, string> = {
  standard: 'Standard',
  rapide: 'Rapide',
  blitz: 'Blitz',
};

export function FideHistoryTable(): JSX.Element {
  const [activeCadence, setActiveCadence] = useState<FideCadence>('rapide');
  const historyQ = useRatingsHistory('fide', activeCadence);
  const deleteM = useDeleteFideRating();

  return (
    <div className="bg-ink-800 border border-ink-700 rounded-xl p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-heading-3 text-paper-50 m-0">Historique FIDE</h3>
        <div className="flex gap-1" role="group" aria-label="Cadence historique">
          {CADENCES.map((c) => {
            const active = c === activeCadence;
            return (
              <button
                type="button"
                key={c}
                onClick={() => setActiveCadence(c)}
                className={`px-2.5 py-1 text-body-sm font-mono uppercase tracking-wider rounded-md border transition-colors ${
                  active
                    ? 'border-acid-500 text-acid-500 bg-ink-900'
                    : 'border-ink-700 text-ink-300 hover:text-paper-50'
                }`}
                aria-pressed={active}
              >
                {CADENCE_LABEL[c]}
              </button>
            );
          })}
        </div>
      </div>

      {historyQ.isLoading && (
        <p className="text-ink-300 font-mono text-body-sm italic py-3">Chargement…</p>
      )}

      {historyQ.data && historyQ.data.points.length === 0 && (
        <p className="text-ink-300 italic py-3">
          Aucun record {CADENCE_LABEL[activeCadence].toLowerCase()} saisi pour le moment.
        </p>
      )}

      {historyQ.data && historyQ.data.points.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {historyQ.data.points.map((p, idx) => (
            <li
              key={`${p.rated_at}-${idx}`}
              className="grid grid-cols-[1fr_auto_auto] gap-3 items-center px-3 py-2 rounded-md border border-ink-700 bg-ink-900"
            >
              <span className="text-paper-50 font-mono tabular-nums text-body-sm">{p.rated_at}</span>
              <span className="font-mono tabular-nums text-paper-50 text-body">{p.value}</span>
              <span className="text-ink-300 text-body-sm italic">
                {/* Suppression désactivée pour l'instant — l'endpoint /history ne
                    retourne pas l'id. Voir backlog T6 follow-up backend. */}
                <DeleteDisabledBadge />
              </span>
            </li>
          ))}
        </ul>
      )}

      <p className="text-[11px] text-ink-300 font-mono italic mt-1">
        Astuce — pour corriger une saisie, contacte Valentin (backlog T6 backend : exposer id dans l'historique).
        <a
          href="https://www.echecs.asso.fr/"
          target="_blank"
          rel="noopener noreferrer"
          className="ml-2 inline-flex items-center gap-1 text-acid-500 hover:underline"
        >
          Site FFE
          <ArrowSquareOut size={12} weight="regular" />
        </a>
      </p>

      {/* deleteM affiché si jamais un id est dispo plus tard — guard for type usage */}
      {deleteM.isError && (
        <p role="alert" className="text-coral-500 text-body-sm">
          Suppression échouée.
        </p>
      )}
    </div>
  );
}

function DeleteDisabledBadge(): JSX.Element {
  return (
    <span
      title="Suppression désactivée (id non exposé par /history)"
      className="inline-flex items-center justify-center text-ink-700"
    >
      <Trash size={14} weight="regular" />
    </span>
  );
}
